import type { JSON } from '@st/sdk'

export type JSONSchema = Record<string, JSON>

export type SchemaRow = {
  path: string
  label: string
  depth: number
  schema: JSONSchema
}

export type SchemaSection = {
  path: string
  label: string
  rows: SchemaRow[]
}

export function buildSchemaSections({
  schema,
  documentData,
  taxReturnData
}: {
  schema: JSONSchema
  documentData?: JSON
  taxReturnData?: JSON
}): SchemaSection[] {
  const properties = asProperties(schema.properties)
  if (!properties) {
    return []
  }

  const sections: SchemaSection[] = []
  let fieldRows: SchemaRow[] = []

  for (const [key, propertySchema] of orderedProperties(properties)) {
    const path = key

    if (isIdSchema(propertySchema)) {
      continue
    }

    if (isContainerSchema(propertySchema)) {
      appendFieldsSection(sections, fieldRows)
      fieldRows = []

      sections.push({
        path,
        label: schemaLabel(key, propertySchema),
        rows: buildRows({
          schema: propertySchema,
          path,
          depth: 1,
          documentData,
          taxReturnData
        })
      })
      continue
    }

    fieldRows.push({ path, label: schemaLabel(key, propertySchema), depth: 0, schema: propertySchema })
  }

  appendFieldsSection(sections, fieldRows)

  return sections
}

export function valueAtPath(data: JSON, path: string): JSON {
  return path.split('.').reduce<JSON>((value, segment) => {
    if (Array.isArray(value)) {
      return value[Number(segment)]
    }

    if (value && typeof value == 'object') {
      return value[segment]
    }

    return undefined
  }, data)
}

export function schemaAtPath(schema: JSONSchema | undefined, path: string): JSONSchema | undefined {
  return path.split('.').reduce<JSONSchema | undefined>((currentSchema, segment) => {
    if (!currentSchema) {
      return undefined
    }

    const properties = asProperties(currentSchema.properties)
    if (properties?.[segment]) {
      return properties[segment]
    }

    if (currentSchema.type == 'array' && isJSONSchema(currentSchema.items)) {
      return currentSchema.items
    }

    return undefined
  }, schema)
}

export function schemaPathLabel(schema: JSONSchema | undefined, path: string) {
  const pathSchema = schemaAtPath(schema, path)
  return pathSchema ? schemaLabel(path, pathSchema) : humanize(path.split('.').at(-1) ?? path)
}

export function formatDataValue(value: JSON) {
  if (value == null || value === '') {
    return '—'
  }

  if (typeof value == 'boolean') {
    return value ? 'Yes' : 'No'
  }

  if (typeof value == 'string' || typeof value == 'number') {
    return String(value)
  }

  return '—'
}

export function schemaLabel(path: string, schema: JSONSchema) {
  const description = schema.description
  if (typeof description == 'string' && description.length > 0) {
    return description
  }

  const title = schema.title
  if (typeof title == 'string' && title.length > 0) {
    return title
  }

  if (schema.type == 'array' && isJSONSchema(schema.items)) {
    const itemTitle = schema.items.title
    if (typeof itemTitle == 'string' && itemTitle.length > 0) {
      return itemTitle
    }
  }

  return humanize(path.split('.').at(-1) ?? path)
}

function buildRows({
  schema,
  path,
  depth,
  documentData,
  taxReturnData
}: {
  schema: JSONSchema
  path: string
  depth: number
  documentData?: JSON
  taxReturnData?: JSON
}): SchemaRow[] {
  const properties = asProperties(schema.properties)
  if (properties) {
    return orderedProperties(properties).flatMap(([key, propertySchema]) => {
      const childPath = `${path}.${key}`

      if (isContainerSchema(propertySchema)) {
        return buildRows({
          schema: propertySchema,
          path: childPath,
          depth: depth + 1,
          documentData,
          taxReturnData
        })
      }

      if (isIdSchema(propertySchema)) {
        return []
      }

      return [
        {
          path: childPath,
          label: schemaLabel(childPath, propertySchema),
          depth,
          schema: propertySchema
        }
      ]
    })
  }

  if (schema.type == 'array' && isJSONSchema(schema.items)) {
    const itemSchema = schema.items
    const itemCount = Math.max(
      1,
      arrayLength(valueAtPath(documentData, path)),
      arrayLength(valueAtPath(taxReturnData, path))
    )

    return Array.from({ length: itemCount }).flatMap((_, index) =>
      buildRows({
        schema: itemSchema,
        path: `${path}.${index}`,
        depth: depth + 1,
        documentData,
        taxReturnData
      })
    )
  }

  if (isIdSchema(schema)) {
    return []
  }

  return [{ path, label: schemaLabel(path, schema), depth, schema }]
}

function isContainerSchema(schema: JSONSchema) {
  return asProperties(schema.properties) != null || schema.type == 'array'
}

function isIdSchema(schema: JSONSchema) {
  return schema.type == 'string' && schema.format == 'uuid'
}

function asProperties(value: JSON): Record<string, JSONSchema> | undefined {
  if (!value || typeof value != 'object' || Array.isArray(value)) {
    return undefined
  }

  return Object.fromEntries(
    Object.entries(value).filter((entry): entry is [string, JSONSchema] => isJSONSchema(entry[1]))
  )
}

function appendFieldsSection(sections: SchemaSection[], rows: SchemaRow[]) {
  if (rows.length > 0) {
    sections.push({ path: `$fields:${rows[0].path}`, label: 'Fields', rows })
  }
}

function orderedProperties(properties: Record<string, JSONSchema>): Array<[string, JSONSchema]> {
  return Object.entries(properties)
    .map((entry, index) => {
      const order = entry[1]['x-order']
      return { entry, order: typeof order == 'number' ? order : index }
    })
    .sort((a, b) => a.order - b.order)
    .map(({ entry }) => entry)
}

function isJSONSchema(value: JSON): value is JSONSchema {
  return Boolean(value && typeof value == 'object' && !Array.isArray(value))
}

function arrayLength(value: JSON) {
  return Array.isArray(value) ? value.length : 0
}

function humanize(value: string) {
  return value.replaceAll('_', ' ').replace(/\b\w/g, (match) => match.toUpperCase())
}
